import * as React from 'react';
import { Badge, Button, Card, Col, Form, Row, Stack } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { AuthShell } from './AuthShell';
import { BillingInterval, PlanOptionId } from './api';

interface Plan {
  id: PlanOptionId;
  audience: string;
  name: string;
  monthlyPrice?: number;
  description: string;
  included: string[];
  cta: string;
  featured?: boolean;
}

const plans: Plan[] = [
  {
    id: 'personal',
    audience: 'Individuals',
    name: 'Starter',
    monthlyPrice: 0,
    description: 'Create, host, and book events.',
    included: ['Free and paid bookings', 'Feedback and certificates', 'Profile and portfolio'],
    cta: 'Start free',
  },
  {
    id: 'pro',
    audience: 'Small teams',
    name: 'Pro',
    monthlyPrice: 14.99,
    description: 'For societies and teams running regular events.',
    included: ['Custom feedback forms', 'Custom certificates', 'Institution homepage', 'Priority support'],
    cta: 'Choose Pro',
  },
  {
    id: 'premium',
    audience: 'Most popular',
    name: 'Premium',
    monthlyPrice: 39.99,
    description: 'For larger organisations needing advanced visibility and reporting.',
    included: ['Advanced analytics', 'Homepage promotion', 'Marketing support', 'Attendance reporting'],
    cta: 'Choose Premium',
    featured: true,
  },
  {
    id: 'enterprise',
    audience: 'Enterprise',
    name: 'Enterprise AI',
    description: 'Workforce and learner event management at scale.',
    included: ['AI timetabling', 'Groups and departments', 'Smart attendance', 'Organisation-wide reporting'],
    cta: 'Contact sales',
  },
];

export const PlanOptions = (props: {
  billingInterval: BillingInterval;
  onBillingIntervalChange: (interval: BillingInterval) => void;
  onSelect: (plan: PlanOptionId) => void;
  currentPlanId?: PlanOptionId;
  disabledPlanIds?: PlanOptionId[];
  ctaLabels?: Partial<Record<PlanOptionId, string>>;
}): React.JSX.Element => (
  <>
    <div className="d-flex justify-content-center mb-4">
      <Form.Check
        type="switch"
        id="annual-billing"
        label="Annual billing (20% saving)"
        checked={props.billingInterval === 'yearly'}
        onChange={(event) => props.onBillingIntervalChange(event.target.checked ? 'yearly' : 'monthly')}
      />
    </div>
    <Row className="g-4">
      {plans.map((plan) => {
        const annualPrice =
          plan.monthlyPrice === undefined ? undefined : plan.monthlyPrice * 12 * 0.8;
        const price = props.billingInterval === 'yearly' ? annualPrice : plan.monthlyPrice;
        return (
          <Col key={plan.id} xs={12} md={6} xl={3}>
            <Card className={`h-100 ${plan.featured ? 'border-primary shadow' : ''}`}>
              <Card.Body className="d-flex flex-column">
                <Stack direction="horizontal" className="justify-content-between align-items-start mb-3">
                  <Badge bg={plan.featured ? 'primary' : 'secondary'}>{plan.audience}</Badge>
                </Stack>
                <Card.Title as="h2" className="h4">
                  {plan.name}
                </Card.Title>
                <p className="text-body-secondary">{plan.description}</p>
                <div className="mb-3">
                  <strong className="fs-3">
                    {price === undefined ? 'Custom' : price === 0 ? '£0' : `£${price.toFixed(2)}`}
                  </strong>
                  {price !== undefined && price > 0 && (
                    <span className="text-body-secondary">
                      {' '}
                      / {props.billingInterval === 'yearly' ? 'year' : 'month'}
                    </span>
                  )}
                </div>
                <ul className="ps-3 flex-grow-1">
                  {plan.included.map((item) => (
                    <li key={item} className="mb-2">
                      {item}
                    </li>
                  ))}
                </ul>
                {plan.id === props.currentPlanId ? (
                  <Button variant="outline-secondary" disabled>
                    Current plan
                  </Button>
                ) : (
                  <Button
                    variant={plan.featured ? 'primary' : 'outline-primary'}
                    disabled={props.disabledPlanIds?.includes(plan.id)}
                    onClick={() => props.onSelect(plan.id)}
                  >
                    {props.ctaLabels?.[plan.id] ?? plan.cta}
                  </Button>
                )}
              </Card.Body>
            </Card>
          </Col>
        );
      })}
    </Row>
    <p className="text-center text-body-secondary small mt-4 mb-0">
      A 6% platform fee applies to paid bookings, which includes payment provider charges.
    </p>
  </>
);

export const WebsitePlanOptionsPage = (): React.JSX.Element => {
  const navigate = useNavigate();
  const [billingInterval, setBillingInterval] = React.useState<BillingInterval>('monthly');

  return (
    <AuthShell
      wide
      title="Choose your plan"
      introduction="Start free or choose the organisation plan that best fits your needs."
    >
      <PlanOptions
        billingInterval={billingInterval}
        onBillingIntervalChange={setBillingInterval}
        onSelect={(plan) =>
          navigate(
            `/auth/user-registration?plan=${plan}&billingInterval=${billingInterval}`,
          )
        }
      />
    </AuthShell>
  );
};
